import {Avatar, ConfigProvider, Drawer, Grid, Layout, Menu, Segmented, Typography} from "antd";
import {
    DashboardOutlined,
    MessageOutlined,
    SettingOutlined,
    MenuOutlined,
    SunOutlined,
    MoonOutlined,
    DesktopOutlined
} from "@ant-design/icons";
import {useLocation, useNavigate} from "react-router";
import {setCollapseSidebar, setTheme, useAppDispatch, useAppSelector} from "@/store";
import {IoListOutline} from "react-icons/io5";
import {GiFroeAndMallet} from "react-icons/gi";
import {getInitials} from "@/utils";


const {Sider} = Layout;
const {useBreakpoint} = Grid;
const {Title, Text} = Typography;

export default function Sidebar() {
    const {collapseSidebar, theme} = useAppSelector(state => state.main);
    const navigate = useNavigate();
    const location = useLocation();
    const screens = useBreakpoint();
    const isMobile = !screens.lg;
    const dispatch = useAppDispatch();
    const { user, dealerUser } = useAppSelector((state) => state.authentication);

    const displayName = [dealerUser?.firstName, dealerUser?.lastName].filter(Boolean).join(" ").trim() || user?.name;
    const avatar = dealerUser?.profile ?? user?.profile;
    const initials = getInitials(displayName);

    const menuItems = [
        {
            key: "/",
            icon: <DashboardOutlined/>,
            label: "Dashboard",
        },
        {
            key: "/listings",
            icon: <IoListOutline/>,
            label: "Listings",
        },
        {
            key: "/auctions",
            icon: <GiFroeAndMallet/>,
            label: "Auctions",
        },
        {
            key: "/messages",
            icon: <MessageOutlined/>,
            label: "Messages",
        },
        {
            key: "/settings",
            icon: <SettingOutlined/>,
            label: "Settings",
        }
    ];

    const handleNavigate = (key: string) => {
        navigate(key);
        if (isMobile) {
            dispatch(setCollapseSidebar(true));
        }
    };



    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: "#00e5ff"
                }
            }}
        >
            {isMobile ? (

                <Drawer
                    title={null}
                    placement="left"
                    open={!collapseSidebar}
                    onClose={() => dispatch(setCollapseSidebar(true))}
                    classNames={{
                        body: "p-0!",
                        header: "hidden!"
                    }}
                    size={300}
                >
                    <div className="flex h-full flex-col bg-white dark:bg-dark-bg">
                        <div className="border-b border-black/5 px-5 py-4 dark:border-white/10">
                            <div className="flex items-center gap-3">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent/10 text-accent">
                                    <MenuOutlined />
                                </div>

                                <div>
                                    <Title level={4} className="mb-0! text-accent">
                                        Navigation
                                    </Title>
                                    <Text type="secondary" className="text-xs">
                                        Browse dashboard sections
                                    </Text>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-3 py-4">
                            <Menu
                                mode="vertical"
                                selectedKeys={[location.pathname]}
                                onClick={({key}) => handleNavigate(key)}
                                items={menuItems}
                                className="rounded-2xl! border-none! bg-transparent! shadow-none!"
                            />
                        </div>

                        <div className=" border-black/5 px-4 py-4 dark:border-white/10">
                            <div className="rounded-2xl bg-gray-50 p-4 dark:bg-dark">
                                <Text type="secondary" className="mb-2 block text-xs uppercase tracking-wide">
                                    Appearance
                                </Text>

                                <Segmented
                                    block
                                    onChange={(value) => dispatch(setTheme(value as "light" | "system" | "dark"))}
                                    shape="round"
                                    options={[
                                        { value: "light", icon: <SunOutlined /> },
                                        { value: "system", icon: <DesktopOutlined /> },
                                        { value: "dark", icon: <MoonOutlined /> },
                                    ]}
                                    value={theme}
                                />
                            </div>

                            {user && (
                                <div className="mt-4 flex items-center gap-3 rounded-2xl bg-gray-50 p-4 dark:bg-dark">
                                    <Avatar shape="circle" size={48} src={avatar}>
                                        {!avatar && initials}
                                    </Avatar>

                                    <div className="min-w-0 flex-1">
                                        <Title level={5} className="my-0! truncate leading-none!">
                                            {displayName}
                                        </Title>
                                        <Text className="block truncate text-sm" type="secondary">
                                            {dealerUser?.email ?? user.email}
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Drawer>
            ) : (
                <Sider
                    collapsed={collapseSidebar}
                    className="top-0 min-h-0 self-start overflow-hidden rounded-2xl! shadow-lg transition-all duration-300 lg:sticky lg:max-h-full"
                >
                    <Menu
                        mode="inline"
                        selectedKeys={[location.pathname]}
                        onClick={({key}) => handleNavigate(key)}
                        items={menuItems}
                        className="rounded-2xl!"
                    />
                </Sider>
            )}
        </ConfigProvider>
    );
}
